import * as React from "react";
import { ChevronsUpDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import useSWR from "swr";
import { usePocket } from "@/contexts/PocketContext";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCity } from "@/contexts/CityContext";
import { useAuth } from "@/contexts/AuthContext";

const SELECTED_CITY_KEY = "selectedCity";

export function TeamSwitcher() {
  const { isMobile } = useSidebar();
  const { pb, user } = usePocket();
  const { selectedCity, setSelectedCity } = useCity();
  
  // Загружаем доступные города в зависимости от роли пользователя
  const { data: availableCities, error } = useSWR(
    "/available-cities",
    async () => {
      // Проверяем, авторизован ли пользователь
      if (!user) return [];
      
      console.log("Загрузка городов для пользователя:", user);
      
      // Для администратора загружаем все города
      if (user.role === 'admin') {
        console.log("Пользователь - администратор, загружаем все города");
        return await pb.collection("cities").getFullList({ sort: "name" });
      }
      
      // Для оператора загружаем только доступные города
      try {
        // Получаем данные оператора
        console.log("Получаем данные оператора с ID:", user.id);
        
        // Получаем оператора с его городами
        const operatorData = await pb.collection("operators").getOne(user.id, {
          expand: "cities",
        });
        
        console.log("Данные оператора:", operatorData);
        
        if (!operatorData.expand?.cities) {
          console.warn("У оператора нет связанных городов");
          return [];
        }
        
        // Проверяем формат данных о городах
        if (Array.isArray(operatorData.expand.cities)) {
          console.log("Найдены города для оператора:", operatorData.expand.cities);
          return operatorData.expand.cities;
        } else {
          // Если только один город
          console.log("Найден один город для оператора:", operatorData.expand.cities);
          return [operatorData.expand.cities];
        }
      } catch (error) {
        console.error("Ошибка при загрузке городов оператора:", error);
        
        // Пробуем альтернативный путь - возможно, связь находится в коллекции users
        try {
          const userData = await pb.collection("users").getOne(user.id, {
            expand: "cities",
          });
          
          console.log("Данные пользователя из коллекции users:", userData);
          
          if (!userData.expand?.cities) {
            console.warn("У пользователя нет связанных городов в коллекции users");
            return [];
          }
          
          if (Array.isArray(userData.expand.cities)) {
            console.log("Найдены города для пользователя в коллекции users:", userData.expand.cities);
            return userData.expand.cities;
          } else {
            console.log("Найден один город для пользователя в коллекции users:", userData.expand.cities);
            return [userData.expand.cities];
          }
        } catch (userError) {
          console.error("Ошибка при загрузке городов из коллекции users:", userError);
          return [];
        }
      }
    },
    {
      revalidateOnFocus: false, // Предотвращаем лишние запросы
    }
  );

  useEffect(() => {
    if (error) {
      console.error("Ошибка при загрузке доступных городов:", error);
    }
  }, [error]);

  // Загружаем все города для резервного плана
  const { data: allCities } = useSWR("/cities", async () => 
    await pb.collection("cities").getFullList({ sort: "name" })
  );

  const [currentTimes, setCurrentTimes] = useState<{ [key: string]: string }>({});

  // Определяем, какие города отображать
  const citiesToDisplay = useMemo(() => {
    // Если есть доступные города, используем их
    if (availableCities && availableCities.length > 0) {
      console.log("Используем доступные города:", availableCities);
      return availableCities;
    }
    
    // Если пользователь администратор и доступны все города, используем их
    if (user?.role === 'admin' && allCities) {
      console.log("Пользователь администратор, используем все города");
      return allCities;
    }
    
    // В крайнем случае, возвращаем пустой массив
    console.log("Нет доступных городов");
    return [];
  }, [availableCities, allCities, user]);

  const activeCityData = useMemo(() => {
    return citiesToDisplay.find((city) => city.id === selectedCity);
  }, [selectedCity, citiesToDisplay]);

  const getCurrentTime = useCallback((timeZone: string): string => {
    const now = new Date();
    const options: Intl.DateTimeFormatOptions = {
      timeZone,
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Intl.DateTimeFormat("ru-RU", options).format(now);
  }, []);

  useEffect(() => {
    if (citiesToDisplay.length > 0) {
      const updateTimes = () => {
        const newTimes = citiesToDisplay.reduce(
          (acc, city) => {
            acc[city.id] = getCurrentTime(city.timezone);
            return acc;
          },
          {} as { [key: string]: string },
        );
        setCurrentTimes(newTimes);
      };

      updateTimes();
      const interval = setInterval(updateTimes, 1000);
      return () => clearInterval(interval);
    }
  }, [citiesToDisplay, getCurrentTime]);

  useEffect(() => {
    const savedCity = localStorage.getItem(SELECTED_CITY_KEY);
    
    if (citiesToDisplay.length > 0) {
      // Если сохраненный город доступен, выбираем его
      if (savedCity && citiesToDisplay.some(city => city.id === savedCity)) {
        setSelectedCity(savedCity);
      } else {
        // Иначе выбираем первый доступный город
        setSelectedCity(citiesToDisplay[0].id);
        localStorage.setItem(SELECTED_CITY_KEY, citiesToDisplay[0].id);
      }
    }
  }, [citiesToDisplay, setSelectedCity]);

  useEffect(() => {
    if (selectedCity) {
      localStorage.setItem(SELECTED_CITY_KEY, selectedCity);
    }
  }, [selectedCity]);

  // Если нет доступных городов, показываем сообщение
  if (citiesToDisplay.length === 0) {
    return (
      <SidebarMenu>
        <SidebarMenuItem>
          <SidebarMenuButton size="lg">
            <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-transparent">
              <div className="size-4" />
            </div>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">Нет доступных городов</span>
              <span className="truncate text-xs">Обратитесь к администратору</span>
            </div>
          </SidebarMenuButton>
        </SidebarMenuItem>
      </SidebarMenu>
    );
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-transparent text-sidebar-primary-foreground">
                {activeCityData ? (
                  <div className="size-full">
                    <img
                      className="w-full h-full object-center object-contain"
                      src={pb.files.getURL(activeCityData, activeCityData.emblem)}
                      alt={activeCityData.name}
                    />
                  </div>
                ) : (
                  <div className="size-4" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{activeCityData ? activeCityData?.name : ""}</span>
                <span className="truncate text-xs">{activeCityData ? currentTimes[activeCityData.id] : ""}</span>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
            align="start"
            side={isMobile ? "bottom" : "right"}
            sideOffset={4}
          >
            <DropdownMenuLabel className="text-xs text-muted-foreground">Доступные города</DropdownMenuLabel>
            {citiesToDisplay.map((city) => (
              <DropdownMenuItem key={city.id} onClick={() => setSelectedCity(city.id)} className="gap-2 p-2">
                <div className="flex size-6 items-center justify-center rounded-sm">
                  <img
                    className="size-full block shrink-0 object-center object-contain"
                    src={pb.files.getURL(city, city.emblem)}
                    alt={city.name}
                  />
                </div>
                {city.name}
                <DropdownMenuShortcut className="tracking-normal">{currentTimes[city.id]}</DropdownMenuShortcut>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

